if (!customElements.get('ugc-slider')) {
  class UgcSlider extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-ugc-viewport]');
      this.track = this.querySelector('[data-ugc-track]');
      this.slides = Array.from(this.querySelectorAll('[data-ugc-slide]'));
      this.prevButton = this.querySelector('[data-ugc-prev]');
      this.nextButton = this.querySelector('[data-ugc-next]');
      this.progressFill = this.querySelector('[data-ugc-progress-fill]');
      this.modal = this.querySelector('[data-ugc-modal]');
      this.modalSlides = Array.from(this.querySelectorAll('[data-ugc-modal-slide]'));
      this.closeButton = this.querySelector('[data-ugc-close]');
      this.muteButton = this.querySelector('[data-ugc-mute]');
      this.modalPrev = this.querySelector('[data-ugc-modal-prev]');
      this.modalNext = this.querySelector('[data-ugc-modal-next]');
      this.activeIndex = 0;
      this.pointerStart = null;
      this.pointerMoved = false;

      if (!this.viewport || !this.slides.length || !this.modal) return;

      this.updateSlider = this.updateSlider.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);

      this.viewport.addEventListener('scroll', this.updateSlider, { passive: true });
      this.prevButton?.addEventListener('click', () => this.scrollBySlide(-1));
      this.nextButton?.addEventListener('click', () => this.scrollBySlide(1));
      this.querySelectorAll('[data-ugc-open]').forEach((button) => {
        button.addEventListener('click', (event) => {
          if (this.pointerMoved) {
            event.preventDefault();
            this.pointerMoved = false;
            return;
          }
          this.openModal(Number(button.dataset.ugcOpen || 0));
        });
      });

      this.viewport.addEventListener('pointerdown', (event) => {
        this.pointerStart = { x: event.clientX, y: event.clientY };
        this.pointerMoved = false;
      });
      this.viewport.addEventListener('pointermove', (event) => {
        if (!this.pointerStart) return;
        if (Math.abs(event.clientX - this.pointerStart.x) > 8 || Math.abs(event.clientY - this.pointerStart.y) > 8) {
          this.pointerMoved = true;
        }
      });
      this.viewport.addEventListener('pointerup', () => {
        this.pointerStart = null;
      });
      this.viewport.addEventListener('pointercancel', () => {
        this.pointerStart = null;
      });

      this.closeButton?.addEventListener('click', () => this.closeModal());
      this.muteButton?.addEventListener('click', () => this.toggleMute());
      this.modalPrev?.addEventListener('click', () => this.showModalSlide(this.activeIndex - 1));
      this.modalNext?.addEventListener('click', () => this.showModalSlide(this.activeIndex + 1));
      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal) this.closeModal();
      });
      document.addEventListener('keydown', this.handleKeydown);

      this.resizeObserver = new ResizeObserver(this.updateSlider);
      this.resizeObserver.observe(this.viewport);

      this.previewObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          entry.target.querySelectorAll('[data-ugc-preview]').forEach((video) => {
            if (entry.isIntersecting && !this.modalOpen) {
              video.play().catch(() => null);
            } else {
              video.pause();
            }
          });
        });
      }, { threshold: 0.15 });
      this.previewObserver.observe(this);

      const modalBackground = window.getComputedStyle(this).getPropertyValue('--ugc-modal-background');
      this.modal.style.setProperty('--ugc-modal-background', modalBackground);
      document.body.appendChild(this.modal);
      requestAnimationFrame(this.updateSlider);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown);
      this.resizeObserver?.disconnect();
      this.previewObserver?.disconnect();
      this.pauseModalVideos();
      this.modal?.remove();
      document.body.classList.remove('ugc-modal-open');
    }

    updateSlider() {
      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const atStart = this.viewport.scrollLeft <= 1;
      const atEnd = this.viewport.scrollLeft >= maxScroll - 1;
      const noOverflow = maxScroll <= 1;

      this.classList.toggle('is-at-start', atStart);
      this.classList.toggle('is-at-end', atEnd);
      this.classList.toggle('has-no-overflow', noOverflow);

      if (this.prevButton) this.prevButton.disabled = atStart;
      if (this.nextButton) this.nextButton.disabled = atEnd;

      if (this.progressFill) {
        const visibleProgress = noOverflow ? 1 : (this.viewport.scrollLeft + this.viewport.clientWidth) / this.viewport.scrollWidth;
        this.progressFill.style.transform = `scaleX(${Math.min(1, Math.max(0, visibleProgress))})`;
      }
    }

    scrollBySlide(direction) {
      const firstSlide = this.slides[0];
      if (!firstSlide) return;
      const trackStyle = window.getComputedStyle(this.track);
      const gap = Number.parseFloat(trackStyle.columnGap || trackStyle.gap || '0') || 0;
      this.viewport.scrollBy({ left: direction * (firstSlide.offsetWidth + gap), behavior: 'smooth' });
    }

    openModal(index) {
      this.modalOpen = true;
      this.modal.hidden = false;
      document.body.classList.add('ugc-modal-open');
      this.querySelectorAll('[data-ugc-preview]').forEach((video) => video.pause());
      this.showModalSlide(index);
      requestAnimationFrame(() => this.closeButton?.focus());
    }

    closeModal() {
      if (!this.modalOpen) return;
      this.modalOpen = false;
      this.modal.hidden = true;
      document.body.classList.remove('ugc-modal-open');
      this.pauseModalVideos();
      this.muteButton.hidden = true;
      this.previewObserver?.unobserve(this);
      this.previewObserver?.observe(this);
    }

    showModalSlide(index) {
      if (!this.modalSlides.length) return;
      this.activeIndex = ((index % this.modalSlides.length) + this.modalSlides.length) % this.modalSlides.length;
      this.pauseModalVideos();

      this.modalSlides.forEach((slide, slideIndex) => {
        slide.hidden = slideIndex !== this.activeIndex;
      });

      const activeSlide = this.modalSlides[this.activeIndex];
      const video = activeSlide?.querySelector('[data-ugc-modal-video]');
      this.muteButton.hidden = !video;

      if (video) {
        video.muted = false;
        this.updateMuteButton(video);
        video.play().catch(() => {
          video.muted = true;
          this.updateMuteButton(video);
          video.play().catch(() => null);
        });
      }
    }

    pauseModalVideos() {
      this.modalSlides?.forEach((slide) => {
        const video = slide.querySelector('[data-ugc-modal-video]');
        if (video) video.pause();
      });
    }

    toggleMute() {
      const video = this.modalSlides[this.activeIndex]?.querySelector('[data-ugc-modal-video]');
      if (!video) return;
      video.muted = !video.muted;
      this.updateMuteButton(video);
    }

    updateMuteButton(video) {
      this.muteButton?.classList.toggle('is-muted', video.muted);
      this.muteButton?.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
    }

    handleKeydown(event) {
      if (!this.modalOpen) return;
      if (event.key === 'Escape') this.closeModal();
      if (event.key === 'ArrowLeft') this.showModalSlide(this.activeIndex - 1);
      if (event.key === 'ArrowRight') this.showModalSlide(this.activeIndex + 1);
    }
  }

  customElements.define('ugc-slider', UgcSlider);
}
